import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Search, IndianRupee, Building2, RefreshCw, AlertCircle,
  Loader2, FileText, X, Calendar, Wrench, Phone
} from 'lucide-react';
import api from '../../utils/api';

const MobileMyPendingPayments = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchPending = useCallback(async () => {
    try {
      const response = await api.get('/services/my-pending-payments');
      setPending(response.data || []);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPending();
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const filtered = pending.filter((s) => {
    const q = searchTerm.toLowerCase();
    return (
      !searchTerm ||
      s.service_no?.toLowerCase().includes(q) ||
      s.customer?.company_name?.toLowerCase().includes(q) ||
      s.customer?.mobile?.includes(searchTerm)
    );
  });

  const totalPending = filtered.reduce((sum, s) => sum + (s.service_charge || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="my-pending-payments-page">
      {/* Header Stats */}
      <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-green-100 text-sm">My Pending Collections</p>
              <p className="text-2xl font-bold">{formatCurrency(totalPending)}</p>
              <p className="text-green-100 text-xs mt-1">{filtered.length} pending payment(s)</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-my-payments-btn"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by service no, customer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="my-payments-search"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <IndianRupee className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No pending payments from your closed services</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => {
            const overdue = isOverdue(s.payment_due_date);
            return (
              <Card
                key={s.id}
                className={`border-l-4 ${overdue ? 'border-l-red-500 bg-red-50/50' : 'border-l-green-500'}`}
                data-testid={`my-payment-card-${s.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-slate-800">{s.service_no}</p>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <Badge className="bg-orange-100 text-orange-700 text-xs">
                          <Wrench className="w-3 h-3 mr-1" />
                          {s.service_type || 'Service'}
                        </Badge>
                        {overdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Overdue
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{formatCurrency(s.service_charge)}</p>
                      <p className="text-xs text-slate-500">pending</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{s.customer?.company_name || 'N/A'}</span>
                    </div>
                    {s.customer?.mobile && (
                      <a href={`tel:${s.customer.mobile}`} className="flex items-center gap-2 text-blue-600">
                        <Phone className="w-4 h-4" />
                        <span>{s.customer.mobile}</span>
                      </a>
                    )}
                    {s.description && (
                      <div className="flex items-start gap-2 text-slate-500">
                        <FileText className="w-4 h-4 mt-0.5" />
                        <span className="text-xs line-clamp-2">{s.description}</span>
                      </div>
                    )}
                    {s.payment_notes && (
                      <p className="text-xs text-slate-500 italic">{s.payment_notes}</p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className={`flex items-center gap-2 ${overdue ? 'text-red-600' : 'text-slate-500'}`}>
                        <Calendar className="w-4 h-4" />
                        <span>Due: {formatDate(s.payment_due_date)}</span>
                      </div>
                      <div className="text-slate-500 text-xs">Closed: {formatDate(s.closed_at)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileMyPendingPayments;
